import {
  ConsultNoteStatus,
  type Brain,
  type GenerationMethod,
  type VoiceStyle,
} from '@heidi/schema/ml-scribe';
import {
  StreamingStatus,
  type CustomStyle,
  type QuerySessionResponse,
} from '@heidi/session/contracts';
import produce from 'immer';
import { cloneDeep } from 'lodash-es';

type ConsultNoteCache = QuerySessionResponse['consult_note'];
export type RequestedNoteAttributes = {
  voiceStyle?: VoiceStyle | null;
  brain?: Brain;
  templateId?: string | null;
  aiCommandText?: string | null;
  generationMethod?: GenerationMethod;
};
export type RestoreSnapshot = Readonly<
  Pick<
    ConsultNoteCache,
    | 'result'
    | 'status'
    | 'voice_style'
    | 'custom_style'
    | 'brain'
    | 'template_id'
    | 'ai_command_text'
    | 'generation_method'
  > & { content_type?: ConsultNoteCache['content_type'] }
>;
export type BaselineRestore = {
  baseline: RestoreSnapshot | undefined;
  previousResult: string | null | undefined;
};
export type StopRestore = BaselineRestore & {
  previousConsultNote: RestoreSnapshot | undefined;
};
export type StreamStart = {
  voiceStyle: VoiceStyle | null | undefined;
  customStyle: CustomStyle | undefined;
  showUploadProgress: boolean | undefined;
  isUploading: boolean | undefined;
};
export type CloseFirstWrite = {
  previousResult: string | null | undefined;
  convertToSectional: boolean;
  isRegeneration: boolean | undefined;
};

export function applyRequestedAttributes(
  old: QuerySessionResponse,
  attrs: RequestedNoteAttributes
): QuerySessionResponse {
  return produce(old, (draft) => {
    draft.feError = undefined;
    draft.consult_note.voice_style = attrs.voiceStyle || old.consult_note.voice_style;
    draft.consult_note.brain = attrs.brain || old.consult_note.brain;
    draft.consult_note.template_id = attrs.templateId || old.consult_note.template_id;
    draft.consult_note.ai_command_text =
      attrs.aiCommandText || old.consult_note.ai_command_text;
    draft.consult_note.generation_method =
      attrs.generationMethod || old.consult_note.generation_method;
  });
}

export function publishGenerationController(
  old: QuerySessionResponse,
  controller: AbortController
): QuerySessionResponse {
  return {
    ...old,
    abortController: controller,
    streamingStatus: StreamingStatus.AWAITING_STREAMING_START,
  };
}

export function markFinishingStreaming(old: QuerySessionResponse): QuerySessionResponse {
  return { ...old, streamingStatus: StreamingStatus.FINISHING_STREAMING };
}

export function restorePreviousNoteOnStop(
  old: QuerySessionResponse,
  restore: StopRestore
): QuerySessionResponse {
  const previous = restore.previousConsultNote;
  // A captured absent field restores absence, rather than keeping the attempt's value.
  return {
    ...old,
    consult_note: {
      ...old.consult_note,
      ...restore.baseline,
      result: restore.previousResult,
      status: previous?.status ?? old.consult_note.status,
      voice_style: previous ? previous.voice_style : old.consult_note.voice_style,
      custom_style: previous ? previous.custom_style : old.consult_note.custom_style,
      brain: previous ? previous.brain : old.consult_note.brain,
      template_id: previous ? previous.template_id : old.consult_note.template_id,
    },
    chunkUploadProgress: undefined,
    streamingStatus: StreamingStatus.MANUALLY_ABORTED,
    abortController: new AbortController(),
  };
}

export function keepStreamedTextOnStop(
  old: QuerySessionResponse,
  streamedText: string
): QuerySessionResponse {
  return {
    ...old,
    consult_note: {
      ...old.consult_note,
      // Web retains the buffer synchronously; desktop leaves it empty.
      result: streamedText !== '' ? streamedText : old.consult_note.result,
      status: ConsultNoteStatus.COMPLETED,
    },
    chunkUploadProgress: undefined,
    streamingStatus: StreamingStatus.MANUALLY_ABORTED,
    abortController: new AbortController(),
  };
}

export function failBeforeStream(
  old: QuerySessionResponse,
  restore: BaselineRestore
): QuerySessionResponse {
  return {
    ...old,
    abortController: new AbortController(),
    streamingStatus: StreamingStatus.NOT_STARTED,
    consult_note: {
      ...old.consult_note,
      ...restore.baseline,
      result: restore.previousResult,
      status: ConsultNoteStatus.FAILED,
    },
  };
}

export function beginStreamingAttempt(
  old: QuerySessionResponse,
  start: StreamStart
): QuerySessionResponse {
  return produce(old, (draft) => {
    draft.consult_note.result = '';
    draft.consult_note.status = ConsultNoteStatus.IN_PROGRESS;
    // Only explicit styles override the previous generation's cache values.
    if (start.voiceStyle != null) draft.consult_note.voice_style = start.voiceStyle;
    if (start.customStyle !== undefined)
      draft.consult_note.custom_style = start.customStyle;
    draft.chunkUploadProgress = start.showUploadProgress ? 0 : undefined;
    draft.streamingStatus = start.isUploading
      ? StreamingStatus.UPLOADING
      : StreamingStatus.AWAITING_STREAMING_START;
  });
}

export function markUploadsFinished(old: QuerySessionResponse): QuerySessionResponse {
  return {
    ...old,
    chunkUploadProgress: undefined,
    streamingStatus: StreamingStatus.AWAITING_STREAMING_START,
  };
}

export function releaseToNotStarted(old: QuerySessionResponse): QuerySessionResponse {
  return {
    ...old,
    abortController: new AbortController(),
    streamingStatus: StreamingStatus.NOT_STARTED,
  };
}

export function resetResultForOpen(old: QuerySessionResponse): QuerySessionResponse {
  const consult_note = cloneDeep(old.consult_note);
  consult_note.result = '';
  return {
    ...old,
    consult_note: { ...consult_note },
    chunkUploadProgress: undefined,
    streamingStatus: StreamingStatus.AWAITING_STREAMING_START,
  };
}

export function appendDesktopChunk(
  old: QuerySessionResponse,
  text: string,
  isJsonTemplate: boolean
): QuerySessionResponse {
  const consult_note = cloneDeep(old.consult_note);
  consult_note.result = text;
  return {
    ...old,
    consult_note: { ...consult_note },
    chunkUploadProgress: undefined,
    streamingStatus: isJsonTemplate
      ? StreamingStatus.AWAITING_STREAMING_START
      : StreamingStatus.STREAMING,
  };
}

export function finishStreamingWithFallback(
  old: QuerySessionResponse,
  close: CloseFirstWrite
): QuerySessionResponse {
  const consult_note = {
    ...cloneDeep(old.consult_note),
    result: !old.consult_note.result ? close.previousResult : old.consult_note.result,
  };
  return {
    ...old,
    chunkUploadProgress: undefined,
    streamingStatus: close.convertToSectional
      ? StreamingStatus.CONVERTING_SECTIONAL_NOTES
      : StreamingStatus.FINISHING_STREAMING,
    abortController: new AbortController(),
    shouldShowDocumentSyncChanges: close.isRegeneration,
    consult_note,
  };
}

export function settleAfterClose(
  old: QuerySessionResponse,
  previousResult: string | null | undefined
): QuerySessionResponse {
  const consult_note = {
    ...cloneDeep(old.consult_note),
    result: !old.consult_note.result ? previousResult : old.consult_note.result,
  };
  return {
    ...old,
    streamingStatus: StreamingStatus.NOT_STARTED,
    abortController: new AbortController(),
    consult_note,
  };
}
